/**
 * Confirmation dialog for cancelling a selection of unsettled payments at once.
 *
 * Lighter than the bulk resource delete on purpose: no typed-back phrase, because cancelling a
 * payment destroys nothing — the customer can open another one on the same order. The reason IS the
 * deliberate keystroke, and it is mandatory for a different reason: this closes rows that money may
 * already have been transferred against, and that sentence is what the customer reads on the
 * payment when they ask why.
 *
 * The backend evaluates each payment on its own, so this dialog has a second life after the
 * request: when part of the batch was skipped it stays open and names those rows, instead of
 * leaving that detail in a toast that disappears.
 */

import { useEffect, useId, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency } from "@/lib/currency";
import { PaymentStatusBadge } from "./PaymentStatusBadge";
import type {
  BulkCancelPaymentsResult,
  PaymentListDto,
} from "@/services/admin-payment.service";

/** Mirrors AdminBulkCancelPaymentsCommandValidator. */
export const CANCEL_REASON_MIN_LENGTH = 5;
export const CANCEL_REASON_MAX_LENGTH = 500;

interface BulkCancelPaymentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The rows the operator picked, in the order the table shows them. */
  payments: PaymentListDto[];
  isPending: boolean;
  /**
   * The report of the last attempt, or null before one has been made. Non-null with skipped rows is
   * what turns this dialog into a result view.
   */
  result: BulkCancelPaymentsResult | null;
  onConfirm: (reason: string) => void;
}

export function BulkCancelPaymentsDialog({
  open,
  onOpenChange,
  payments,
  isPending,
  result,
  onConfirm,
}: BulkCancelPaymentsDialogProps) {
  const reasonFieldId = useId();
  const [reason, setReason] = useState("");
  /**
   * A cancel request is outstanding.
   *
   * `isPending` is the parent's mutation state and only arrives on the NEXT render, so two clicks
   * in the same tick both read `canCancel` as true and both fire — the second batch races the
   * first over the same rows, and the report the operator sees back is whichever request's
   * response lands last.
   */
  const confirmInFlight = useRef(false);

  // A fresh open starts empty, so one batch's reason can never be submitted against another
  // selection.
  useEffect(() => {
    if (open) {
      setReason("");
      confirmInFlight.current = false;
    }
  }, [open]);

  // This dialog does not own the request, so it has no `finally` to release the latch in — the
  // parent's mutation settling is the signal.
  useEffect(() => {
    if (!isPending) {
      confirmInFlight.current = false;
    }
  }, [isPending]);

  const trimmedReason = reason.trim();
  const reasonTooShort = trimmedReason.length < CANCEL_REASON_MIN_LENGTH;
  const canCancel = !isPending && payments.length > 0 && !reasonTooShort;

  const skipped = result?.results.filter((row) => !row.success) ?? [];
  const showingResult = skipped.length > 0;

  const handleConfirm = () => {
    if (confirmInFlight.current || !canCancel) {
      return;
    }

    confirmInFlight.current = true;
    onConfirm(trimmedReason);
  };

  return (
    <>
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          // Cancel is disabled while the request runs, so closing must be blocked too.
          if (isPending && !next) {
            return;
          }

          onOpenChange(next);
        }}
      >
        <AlertDialogContent
          className="max-w-lg"
          onEscapeKeyDown={(event) => {
            if (isPending) {
              event.preventDefault();
            }
          }}
        >
          {showingResult ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {result!.successCount > 0
                    ? `${result!.successCount} of ${result!.totalRequested} payments cancelled`
                    : "No payments were cancelled"}
                </AlertDialogTitle>
              </AlertDialogHeader>

              <AlertDialogBody className="space-y-4">
                <AlertDialogDescription>
                  {`${skipped.length} payment${skipped.length === 1 ? " was" : "s were"} left as ${skipped.length === 1 ? "it is" : "they are"}. `}
                  A payment that settled while you were choosing is skipped on
                  purpose — check it before trying again.
                </AlertDialogDescription>
                <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border p-2">
                  {skipped.map((row) => (
                    <div key={row.paymentId} className="space-y-0.5">
                      <p className="truncate text-sm font-medium">
                        {row.paymentOrderNumber || row.paymentId}
                      </p>
                      <p className="text-xs text-destructive-ink">
                        {row.errorMessage ??
                          row.errorCode ??
                          "This payment could not be cancelled."}
                      </p>
                    </div>
                  ))}
                </div>
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {/* One payment is also how a row's own menu opens this, where nothing is "selected". */}
                  {payments.length === 1
                    ? "Cancel this payment?"
                    : `Cancel ${payments.length} selected payments?`}
                </AlertDialogTitle>
              </AlertDialogHeader>

              <AlertDialogBody className="space-y-4">
                <AlertDialogDescription>
                  They can no longer be verified. No money moves.
                </AlertDialogDescription>
                {/* The whole selection, not a count: this runs across tenants, and a number alone
                cannot show that one of the rows belongs to the wrong customer. */}
                <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
                  {payments.map((payment) => (
                    <div
                      key={payment.paymentId}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {payment.paymentOrderNumber}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {payment.tenantName}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {formatCurrency(payment.amount, payment.currency)}
                        </span>
                        {/* The list's own badge, not a bare one: it spaces and colours the status
                        the way every other payment screen does, so "Awaiting Verification" does
                        not read as one word here and two words everywhere else. */}
                        <PaymentStatusBadge status={payment.status} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* The one thing this action can get wrong that a reload will not fix. */}
                <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-ink" />
                  <p className="text-xs text-warning-ink">
                    Cancel only what you are sure was never paid.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={reasonFieldId}>
                    Reason for cancelling{" "}
                    <span className="text-destructive-ink" aria-hidden="true">
                      *
                    </span>
                  </Label>
                  <Textarea
                    id={reasonFieldId}
                    required
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Why are these payments being cancelled?"
                    maxLength={CANCEL_REASON_MAX_LENGTH}
                    rows={3}
                    disabled={isPending}
                    aria-invalid={reason.length > 0 && reasonTooShort}
                  />
                  <p className="text-xs text-muted-foreground">
                    {`At least ${CANCEL_REASON_MIN_LENGTH} characters. The customer sees it on the payment.`}
                  </p>
                </div>
              </AlertDialogBody>

              <AlertDialogFooter>
                <AlertDialogCancel disabled={isPending}>Back</AlertDialogCancel>
                <Button
                  variant="destructive"
                  onClick={handleConfirm}
                  disabled={!canCancel}
                >
                  {isPending ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4" />
                      Cancelling…
                    </>
                  ) : (
                    `Cancel ${payments.length} payment${payments.length === 1 ? "" : "s"}`
                  )}
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
      {/* No overlay of its own: the table that opens this covers its own Card while the batch runs
        (PaymentListTable), so the page around the list stays usable. */}
    </>
  );
}
